import React, { createContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Label } from '@components';
import { checkChildren, filterChildrenOnType } from '@hooks';
import { mergeClasses } from '@utils';

import ProductCardFavorite from './ProductCard.Favorite';
import ProductCardImage from './ProductCard.Image';
import ProductCardInformation from './ProductCard.Information';
import ProductCardLabels from './ProductCard.Labels';
import ProductCardPrice from './ProductCard.Price';
import ProductCardRatings from './ProductCard.Ratings';
import ProductCardTitle from './ProductCard.Title';

export const ProductCardContext = createContext();

/**
 * ProductCard component that serves as a container for its child components:
 * `ProductCard.Favorite`, `ProductCard.Image`, `ProductCard.Labels`, `ProductCard.Ratings`, `ProductCard.Title`, and `ProductCard.Price`.
 * Requires at least one child of type `ProductCard.Image` and `ProductCard.Title`.
 *
 * @component ProductCard
 * @param {Object} props - The properties for the ProductCard component.
 * @param {React.ReactNode} props.children - The child components of the ProductCard. Must include at least one `ProductCard.Image` and `ProductCard.Title` child component.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the ProductCard container.
 * @param {string} props.href - The URL of the Product Detail Page.
 * @param {boolean} [props.isExclusive] - Optional Flag to indicate if the Product is Exclusive.
 * @param {boolean} [props.isLimited] - Optional Flag to indicate if the Product is Limited.
 * @param {boolean} [props.isPreorder] - Optional Flag to indicate if the Product is available for Preorder.
 * @param {number} props.productId - The ID for the Product.
 * @param {Object} [restProps] - Additional properties to be passed to the ProductCard container.
 * @returns {ReactElement} The rendered ProductCard component.
 * @example
 * <ProductCard
 *   href="#"
 *   productId={266712}
 *   isExclusive={true}
 *   isLimited={false}
 *   isPreorder={false}
 * >
 *   <ProductCard.Image src="https://thumblr.uniid.it/product/266712/fdf6817198ad.jpg" alt="Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6" />
 *   <ProductCard.Title>Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6</ProductCard.Title>
 * </ProductCard>
 */
export default function ProductCard({
  children,
  className,
  href,
  isExclusive,
  isLimited,
  isPreorder,
  productId,
  ...restProps
}) {
  checkChildren(children, [
    ProductCard.Favorite,
    ProductCard.Image,
    ProductCard.Information,
    ProductCard.Labels,
    ProductCard.Ratings,
    ProductCard.Title,
    ProductCard.Price
  ]);

  const [favoriteChild, nonFavoriteChildren] = filterChildrenOnType(
    [ProductCard.Favorite],
    children
  );

  const [isDiscounted, setDiscounted] = useState(false);

  const contextValue = useMemo(
    () => ({
      isDiscounted,
      isExclusive,
      isLimited,
      isPreorder,
      productId,
      setDiscounted
    }),
    [isDiscounted, isLimited, isPreorder, productId, setDiscounted]
  );

  let hasImage = false;
  let hasTitle = false;

  React.Children.forEach(children, child => {
    if (child.type === ProductCard.Image) {
      hasImage = true;
    }
    if (child.type === ProductCard.Title) {
      hasTitle = true;
    }
  });

  if (!hasImage || !hasTitle) {
    throw new Error(
      "Missing required components in 'ProductCard'. 'ProductCard.Image' and 'ProductCard.Title' components are required."
    );
  }

  return (
    <ProductCardContext.Provider value={contextValue}>
      <article
        className={mergeClasses(
          'relative rounded-2xl bg-white @container',
          className
        )}
        {...restProps}
      >
        {favoriteChild}

        <a
          href={href}
          className="no-underline focus:outline-none"
          draggable={false}
        >
          <div className="flex flex-col gap-y-1 last:pb-2 @md:gap-y-2">
            {nonFavoriteChildren}
          </div>
        </a>
      </article>
    </ProductCardContext.Provider>
  );
}

ProductCard.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  href: PropTypes.string.isRequired,
  isExclusive: PropTypes.bool,
  isLimited: PropTypes.bool,
  isPreorder: PropTypes.bool,
  productId: PropTypes.number.isRequired
};

ProductCard.defaultProps = {
  className: '',
  isExclusive: false,
  isLimited: false,
  isPreorder: false
};

ProductCard.Favorite = ProductCardFavorite;
ProductCard.Image = ProductCardImage;
ProductCard.Information = ProductCardInformation;
ProductCard.Labels = ProductCardLabels;
ProductCard.Labels.Label = Label;
ProductCard.Price = ProductCardPrice;
ProductCard.Ratings = ProductCardRatings;
ProductCard.Title = ProductCardTitle;
